import React, { useEffect, useState } from "react";
import {
  DateOrTimeView,
  DateTimePicker,
  DatePicker as MuiDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { Box, useTheme } from "@mui/material";
import { Dayjs } from "dayjs";
import { observer } from "mobx-react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import "dayjs/locale/en-gb";

const DATE_VIEWS = ["year", "month", "day"] as const;
const DATETIME_VIEWS: readonly DateOrTimeView[] = [
  "year",
  "month",
  "day",
  "hours",
  "minutes",
  "seconds",
];

interface DatePicker2Props {
  type?: "date" | "datetime";
  label: string;
  value: Dayjs | null;
  onChange?: (newValue: Dayjs | null) => void;
  error?: boolean;
  disabled?: boolean;
  borderColor?: string;
  backgroundColor?: string;
  fontSize?: string;
  require?: boolean;
  zIndex?: number;
  minDate?: Dayjs;
  minDateTime?: Dayjs;
  maxDate?: Dayjs;
  borderRadius?: string;
}

const DatePicker2: React.FC<DatePicker2Props> = observer(
  ({
    type = "datetime",
    label,
    value,
    onChange,
    error = false,
    disabled = false,
    borderColor = "#dddddd",
    backgroundColor = "#FFF",
    fontSize = "13px",
    borderRadius,
    require = false,
    zIndex = 3000,
    minDate,
    minDateTime,
    maxDate,
  }) => {
    const theme = useTheme();
    const isError = error && require && !value;
    const [currentDate, setCurrentDate] = useState<Dayjs | null>(value);
    const isDateOnly = type === "date";

    useEffect(() => {
      setCurrentDate(value);
    }, [value]);

    const handleDateChange = (newValue: Dayjs | null) => {
      if (onChange) {
        onChange(newValue);
        setCurrentDate(newValue);
      }
    };

    const sharedSlotProps = {
      popper: {
        sx: {
          zIndex: zIndex,
        },
      },
      dialog: {
        sx: {
          zIndex: zIndex,
        },
      },
      ...(label
        ? {}
        : {
            textField: {
              InputLabelProps: { shrink: false, sx: { display: "none" } },
            },
          }),
    };

    const sharedSx = {
      width: "100%",
      background: backgroundColor,
      "& .MuiInputBase-root": {
        height: "37px",
        width: "inherit",
        borderRadius: borderRadius,
      },
      "& .MuiOutlinedInput-root": {
        width: "100%",
        fontSize,
        "& fieldset": {
          borderColor: isError
            ? theme.palette.customColors.borderErrorRedColor
            : disabled
              ? theme.palette.customColors.disabledBorderColor
              : borderColor,
        },
        "&:hover fieldset": {
          borderColor: isError
            ? theme.palette.customColors.borderErrorRedColor
            : theme.palette.customColors.purple,
        },
        "&.Mui-focused fieldset": {
          borderColor: isError
            ? theme.palette.customColors.borderErrorRedColor
            : theme.palette.customColors.purple,
          borderWidth: "1px",
        },
        "&.Mui-disabled": {
          backgroundColor:
            theme.palette.customColors?.greyBodyBackground || "#f5f5f5",
        },
      },
      "& .MuiInputLabel-root": {
        fontSize: fontSize,
        top: "50%",
        transform: "translateY(-50%)",
        left: 10,
        color: disabled
          ? theme.palette.customColors.disabledBorderColor
          : theme.palette.customColors.tableGrayHeadingColor,
        "&.Mui-focused": {
          color: isError
            ? theme.palette.customColors.borderErrorRedColor
            : theme.palette.customColors.purple,
        },
      },
      "& .MuiInputLabel-shrink": {
        top: "-16px",
        transform: "translateY(0)",
      },
      "& .MuiInputBase-input": {
        color: disabled
          ? theme.palette.customColors.disabledBorderColor
          : theme.palette.customColors.darkBlackBorder,
        fontSize,
      },
      "& .MuiFormHelperText-root": {
        color: theme.palette.customColors.borderErrorRedColor,
        fontSize: "12px",
        marginTop: "4px",
      },
    };

    return (
      <Box sx={{ flex: "1 1 100%" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          {isDateOnly ? (
            <MuiDatePicker
              views={DATE_VIEWS}
              label={label}
              key={label}
              value={currentDate}
              onChange={handleDateChange}
              disabled={disabled}
              minDate={minDate}
              maxDate={maxDate}
              format="DD/MM/YYYY"
              slotProps={sharedSlotProps}
              sx={sharedSx}
            />
          ) : (
            <DateTimePicker
              views={DATETIME_VIEWS}
              label={label}
              key={label}
              value={currentDate}
              onChange={handleDateChange}
              disabled={disabled}
              minDate={minDate}
              minDateTime={minDateTime}
              maxDate={maxDate}
              slotProps={sharedSlotProps}
              sx={sharedSx}
            />
          )}
        </LocalizationProvider>
      </Box>
    );
  }
);

export default DatePicker2;
